import { useState } from 'react'
import { useNavigate } from 'react-router'
import { KpiTile, Panel, SectionHead, Tabs } from '@/components/redesign/bits'
import { VIZ } from '@/components/redesign/chart-utils'
import { BarList, Legend, StackedBars, type StackKey } from '@/components/redesign/charts'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { numberFmt } from '@/lib/format'
import { type DataSpend, type SpendDays, useDataSpend } from './api'

const usd = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' })

/** Provider costs are fractions of a cent per call, so small amounts keep their cents. */
export function formatSpend(v: number) {
  return v > 0 && v < 0.01 ? '<$0.01' : usd.format(v)
}

const PERIODS: { key: `${SpendDays}`; label: string }[] = [
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
  { key: '90', label: '90 days' },
]

const dayLabel = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

/**
 * Owners: what paid data calls cost Arva SEO each day, what the money went on and who used it, and
 * what customers use their AI credits for. Each call is recorded with its cost when it's made.
 */
export function DataSpendPanels() {
  const [period, setPeriod] = useState<`${SpendDays}`>('30')
  const spend = useDataSpend(Number(period) as SpendDays)

  return (
    <>
      <SectionHead
        title="Data spend"
        small="paid data calls and AI credits"
        extra={<Tabs label="Period" value={period} onChange={setPeriod} tabs={PERIODS} />}
      />
      {spend.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : spend.isError ? (
        <div className="card">
          <ErrorState error={spend.error} onRetry={() => void spend.refetch()} />
        </div>
      ) : (
        <Spend data={spend.data.data} />
      )}
    </>
  )
}

function Spend({ data }: { data: DataSpend }) {
  const navigate = useNavigate()
  const openCustomer = (id: string) => void navigate(`/customers/${id}`)
  const ai = data.ai_credits
  // The four providers that cost most get their own colour; any others share grey.
  const top = data.providers.slice(0, 4)
  const keys: StackKey[] = top.map((p, i) => ({
    key: p.key,
    label: p.label,
    color: VIZ[i] ?? 'var(--viz-other)',
  }))
  const others = data.providers.length > top.length
  if (others) keys.push({ key: 'other', label: 'Other', color: 'var(--viz-other)' })
  const rows = data.series.map((d) => {
    const values: Record<string, number> = {}
    for (const [provider, cost] of Object.entries(d.by_provider)) {
      const key = top.some((p) => p.key === provider) ? provider : 'other'
      values[key] = (values[key] ?? 0) + cost
    }
    return { label: dayLabel(d.date), values }
  })
  const perDay = data.total / data.days

  return (
    <>
      <div className="kpis">
        <KpiTile
          label="Spent"
          value={formatSpend(data.total)}
          now={data.total}
          previous={data.previous_total}
          invert
          compare={`vs the ${data.days} days before`}
          spark={data.series.map((d) => d.total)}
        />
        <KpiTile label="Per day" value={formatSpend(perDay)} compare={`${data.days}-day average`} />
        <KpiTile label="Paid calls" value={numberFmt.format(data.calls)} compare={`in ${data.days} days`} />
        <KpiTile
          label="AI credits used"
          value={numberFmt.format(ai.credits)}
          compare={`${formatSpend(ai.cost)} · ${ai.workspaces} workspace${ai.workspaces === 1 ? '' : 's'}`}
        />
      </div>

      <Panel
        title="Spend by day"
        sub="What each day’s paid data calls cost, by provider (US dollars)"
        extra={keys.length > 0 ? <Legend keys={keys} /> : undefined}
      >
        {data.calls === 0 ? (
          <p className="text-muted text-[13px]">No paid data calls in these {data.days} days.</p>
        ) : (
          <StackedBars rows={rows} keys={keys} format={formatSpend} label="Data spend by day and provider" />
        )}
      </Panel>

      <div className="grid2">
        <Panel title="What it went on" sub="Data calls, most expensive first (AI credits are below)">
          {data.operations.length === 0 ? (
            <p className="text-muted text-[13px]">No data calls yet.</p>
          ) : (
            <BarList
              rows={data.operations.map((o) => ({
                key: o.key,
                label: o.label,
                value: o.cost,
                sub: `${numberFmt.format(o.calls)} call${o.calls === 1 ? '' : 's'}`,
              }))}
              format={formatSpend}
              labelWidth={190}
              valueWidth={74}
            />
          )}
        </Panel>
        <Panel title="Who used it" sub="Workspaces that cost most, AI credits included">
          {data.workspaces.length === 0 ? (
            <p className="text-muted text-[13px]">No workspace has used paid data yet.</p>
          ) : (
            <BarList
              rows={data.workspaces.map((w) => ({ key: w.id, label: w.name, value: w.cost, sub: w.plan }))}
              format={formatSpend}
              labelWidth={190}
              valueWidth={74}
              onSelect={openCustomer}
            />
          )}
        </Panel>
      </div>

      <div className="grid2">
        <Panel
          title="What AI credits are used for"
          sub={`${numberFmt.format(ai.uses)} AI request${ai.uses === 1 ? '' : 's'}, by credits used`}
        >
          {ai.actions.length === 0 ? (
            <p className="text-muted text-[13px]">Nobody used AI credits in these {data.days} days.</p>
          ) : (
            <BarList
              rows={ai.actions.map((a) => ({
                key: a.key,
                label: a.label,
                value: a.credits,
                color: 'var(--viz-2)',
                sub: `${numberFmt.format(a.uses)}× · ${a.workspaces} workspace${a.workspaces === 1 ? '' : 's'} · ${formatSpend(a.cost)}`,
              }))}
              labelWidth={210}
              valueWidth={150}
            />
          )}
        </Panel>
        <Panel
          title="Who uses AI credits"
          sub={`${ai.own_key_workspaces} workspace${ai.own_key_workspaces === 1 ? '' : 's'} also connected their own OpenAI or Anthropic key, billed by that provider and not counted here`}
        >
          {ai.top_workspaces.length === 0 ? (
            <p className="text-muted text-[13px]">No workspace used AI credits yet.</p>
          ) : (
            <BarList
              rows={ai.top_workspaces.map((w) => ({
                key: w.id,
                label: w.name,
                value: w.credits ?? 0,
                color: 'var(--viz-2)',
                sub: `${w.plan} · ${formatSpend(w.cost)}`,
              }))}
              labelWidth={190}
              valueWidth={110}
              onSelect={openCustomer}
            />
          )}
        </Panel>
      </div>
    </>
  )
}
